import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import {
    Route as RouteIcon, Plus, Check, Undo2, Flag, ChevronDown, ChevronRight, AlertTriangle, RotateCw, Loader2,
} from 'lucide-react';
import ProgrammeTimeline from './ProgrammeTimeline';
import StartProgrammeModal from './StartProgrammeModal';
import TickMilestoneModal from './TickMilestoneModal';
import CloseProgrammeModal from './CloseProgrammeModal';
import useProgrammeAccess from './useProgrammeAccess';
import { fetchPatientProgrammes, tickMilestone } from '../../redux/programmeSlice';
import { serverReason } from '../patientTabs/prescriptionHelpers';
import { stopLabel } from '../../utils/careProgrammes';

// THE "PROGRAMME" CARD — on the patient's page and in the visit (every
// status), for the doctor and the hospital admin (care programmes; a
// programme is a LINE OF VISITS since 21 Oct 2026, doc/programme-visits.md).
// Start a programme (one of the doctor's own or the hospital's, on a start
// date), follow it as THE STOP LINE (Start, Stop 1, Stop 2… each done /
// booked / open / upcoming / late, the booked date, an emergency's reason),
// tick a stop by hand with a note (or untick a hand tick), and Close it
// (outcome; the doctor may add a life event to the record). Active
// programmes first; closed ones collapsed below.
//
// IN THE VISIT a patient with NO running programme gets no card at all — only
// a small "+ Programme" button, for the same people today's Start is for,
// opening the same start dialog (owner, 21 Oct 2026).
//
// Check sets are NOT part of a programme (owner: "don't collapse check sets
// with programmes") — no "Record <set>" here; readings stay in the visit's
// own Checks block and never tick a stop.
//
// THE DESK STRIP (`variant="desk"`, or staff): the active programmes' stop
// line, read-only — staff with `checks.record`, and the admin at the desk.
// The server gives staff the `timeline` projection (no notes, no ids) and
// only while the patient has an open visit here.
//
// Nothing is drawn when the hospital has no care_programmes, or when the
// server says this viewer may not read the patient's programmes (a doctor
// not linked to the patient, staff without an open visit) — those are not
// the viewer's problem to read.

const QUIET_CODES = new Set([
    'NOT_LINKED_DOCTOR', 'NO_OPEN_VISIT', 'FEATURE_DISABLED', 'PERMISSION_DENIED', 'STAFF_ACCESS_DENIED',
    'PROGRAMMES_ROLE_REFUSED', 'PATIENT_NOT_FOUND', 'FORBIDDEN',
]);

function ProgrammeCard({ patientId, appointment = null, variant = 'auto', className = '' }) {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const access = useProgrammeAccess();
    const myId = typeof localStorage !== 'undefined' ? localStorage.getItem('ID') : null;
    const role = access.role;
    const desk = variant === 'desk' ? access.desk : (role === 'user' && access.desk);
    const full = !desk && variant !== 'desk' && access.clinician;
    const enabled = Boolean(patientId && access.featureOn && (desk || full));
    const appointmentId = appointment?.appointmentId || null;
    const ownVisit = role === 'doctor' && appointment && String(appointment.doctorId) === String(myId);

    const entry = useSelector((state) => (patientId ? state.programmes?.patients?.[patientId] : null));
    const acting = useSelector((state) => state.programmes?.acting);

    const [starting, setStarting] = useState(false);
    const [ticking, setTicking] = useState(null);
    const [closing, setClosing] = useState(null);
    const [showClosed, setShowClosed] = useState(false);
    const [openClosed, setOpenClosed] = useState({});

    const load = () => dispatch(fetchPatientProgrammes({ patientId, status: desk ? 'active' : 'all' }));

    useEffect(() => {
        if (enabled) load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled, patientId, desk]);

    const active = entry?.active || [];
    const closed = desk ? [] : (entry?.closed || []);

    if (!enabled || !entry) return null;
    if (entry.error && (desk || QUIET_CODES.has(entry.error.code))) return null;
    if (desk && active.length === 0) return null;

    const startAppointmentId = appointment && appointment.status !== 'Cancelled' && (role === 'admin' || ownVisit) ? appointmentId : null;
    const startModal = full ? (
        <StartProgrammeModal
            isOpen={starting}
            patientId={patientId}
            appointmentId={startAppointmentId}
            defaultDoctorId={role === 'admin' ? (appointment?.doctorId || null) : null}
            runningIds={active.map((p) => p.programmeId)}
            onClose={() => setStarting(false)}
        />
    ) : null;

    // IN THE VISIT, NOTHING RUNNING: the small "+ Programme" button, no card.
    if (full && appointment && entry.loaded && !entry.error && active.length === 0) {
        return (
            <div data-testid="programme-add-row" className={`flex justify-end ${className}`}>
                <button
                    type="button"
                    data-testid="programme-add"
                    onClick={() => setStarting(true)}
                    className="inline-flex items-center gap-1 rounded-full border border-[#4ABDBD]/40 bg-white px-3 py-1 text-xs font-semibold text-[#2D8A8A] shadow-sm transition-colors hover:bg-[#4ABDBD]/10"
                >
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('programmes.add_programme')}
                </button>
                {startModal}
            </div>
        );
    }
    if (full && appointment && !entry.loaded && !entry.error) return null;

    const untick = async (p, m) => {
        const answer = await Swal.fire({
            title: t('programmes.untick_title'),
            text: t('programmes.untick_text', { name: m.name }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('programmes.untick_confirm'),
            cancelButtonText: t('programmes.cancel'),
            confirmButtonColor: '#2D8A8A',
            cancelButtonColor: '#9ca3af',
        });
        if (!answer?.isConfirmed) return;
        const res = await dispatch(tickMilestone({ patientId, patientProgrammeId: p.patientProgrammeId, key: m.key, done: false }));
        if (tickMilestone.fulfilled.match(res)) toast.success(t('programmes.unticked', { name: m.name }));
        else toast.error(serverReason(res.payload, t('programmes.tick_failed'), i18n.language));
    };

    const btn = 'inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-semibold transition-colors disabled:opacity-50';
    // Tick / untick on a STOP (the Start is not a stop to tick).
    const milestoneActions = (p) => (stop) => {
        if (!full || p.status !== 'active' || stop.kind !== 'stop') return null;
        const busy = acting === p.patientProgrammeId;
        const named = { ...stop, name: stop.name || stopLabel(stop, t) };
        if (stop.state !== 'done') {
            return (
                <button
                    type="button"
                    data-testid={`programme-tick-${p.patientProgrammeId}-${stop.key}`}
                    onClick={() => setTicking({ programme: p, milestone: named })}
                    disabled={busy}
                    className={`${btn} border border-gray-200 text-gray-600 hover:border-[#4ABDBD] hover:text-[#2D8A8A]`}
                >
                    <Check className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('programmes.tick')}
                </button>
            );
        }
        if (stop.doneBy === 'tick') {
            return (
                <button
                    type="button"
                    data-testid={`programme-untick-${p.patientProgrammeId}-${stop.key}`}
                    onClick={() => untick(p, named)}
                    disabled={busy}
                    className={`${btn} text-gray-500 hover:bg-gray-100`}
                >
                    <Undo2 className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('programmes.untick')}
                </button>
            );
        }
        return null;
    };

    const closeButton = (p) => (full && p.status === 'active' ? (
        <button
            type="button"
            data-testid={`programme-close-${p.patientProgrammeId}`}
            onClick={() => setClosing(p)}
            disabled={acting === p.patientProgrammeId}
            className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-semibold text-gray-600 hover:border-[#2D8A8A] hover:text-[#2D8A8A] disabled:opacity-50"
        >
            {acting === p.patientProgrammeId ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" /> : <Flag className="h-3.5 w-3.5" aria-hidden="true" />}
            {t('programmes.close_programme')}
        </button>
    ) : null);

    return (
        <section
            data-testid={desk ? 'programme-desk-strip' : 'programme-card'}
            className={`rounded-2xl border border-[#4ABDBD]/25 bg-gradient-to-br from-[#4ABDBD]/5 to-white p-4 ${className}`}
        >
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <h3 className="flex items-center gap-2 text-sm font-bold text-[#2D8A8A]">
                    <RouteIcon className="h-4 w-4" aria-hidden="true" />
                    {desk ? t('programmes.desk_title') : t('programmes.card_title')}
                    {entry.loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-[#4ABDBD]" aria-hidden="true" />}
                </h3>
                {full && (
                    <button
                        type="button"
                        data-testid="programme-start"
                        onClick={() => setStarting(true)}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] px-3 py-1.5 text-xs font-semibold text-white shadow transition-all hover:shadow-md"
                    >
                        <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('programmes.start_programme')}
                    </button>
                )}
            </div>

            {entry.error ? (
                <div role="alert" data-testid="programme-card-error" className="flex flex-wrap items-center gap-2 text-xs text-red-700">
                    <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                    {serverReason(entry.error, t('programmes.load_failed'), i18n.language)}
                    <button type="button" data-testid="programme-card-retry" onClick={load} className="inline-flex items-center gap-1 rounded-lg px-2 py-0.5 font-semibold text-[#2D8A8A] hover:bg-[#4ABDBD]/10">
                        <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                        {t('programmes.retry')}
                    </button>
                </div>
            ) : !entry.loaded ? (
                <p className="flex items-center gap-2 text-xs text-[#4ABDBD]">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    {t('programmes.loading')}
                </p>
            ) : (
                <div className="flex flex-col gap-3">
                    {active.length === 0 && (
                        <p data-testid="programme-card-empty" className="text-xs text-gray-500">{t('programmes.none_running')}</p>
                    )}
                    {active.map((p) => (
                        <ProgrammeTimeline
                            key={p.patientProgrammeId}
                            programme={p}
                            scope={entry.scope || (desk ? 'timeline' : 'full')}
                            headerActions={closeButton(p)}
                            milestoneActions={milestoneActions(p)}
                        />
                    ))}
                    {closed.length > 0 && (
                        <div data-testid="programme-card-closed">
                            <button
                                type="button"
                                data-testid="programme-card-closed-toggle"
                                aria-expanded={showClosed}
                                onClick={() => setShowClosed((v) => !v)}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-[#2D8A8A]"
                            >
                                {showClosed ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ChevronRight className="h-4 w-4" aria-hidden="true" />}
                                {t('programmes.closed_programmes', { n: closed.length })}
                            </button>
                            {showClosed && (
                                <div className="mt-2 flex flex-col gap-2">
                                    {closed.map((p) => {
                                        const open = Boolean(openClosed[p.patientProgrammeId]);
                                        return (
                                            <div key={p.patientProgrammeId}>
                                                <ProgrammeTimeline
                                                    programme={p}
                                                    scope={entry.scope || 'full'}
                                                    compact={!open}
                                                    headerActions={(
                                                        <button
                                                            type="button"
                                                            data-testid={`programme-closed-expand-${p.patientProgrammeId}`}
                                                            aria-expanded={open}
                                                            onClick={() => setOpenClosed((prev) => ({ ...prev, [p.patientProgrammeId]: !open }))}
                                                            className="rounded-lg px-2 py-1 text-[11px] font-semibold text-[#2D8A8A] hover:bg-[#4ABDBD]/10"
                                                        >
                                                            {open ? t('programmes.hide_milestones') : t('programmes.show_milestones')}
                                                        </button>
                                                    )}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {full && (
                <>
                    {startModal}
                    <TickMilestoneModal
                        isOpen={Boolean(ticking)}
                        patientId={patientId}
                        programme={ticking?.programme}
                        milestone={ticking?.milestone}
                        onClose={() => setTicking(null)}
                    />
                    <CloseProgrammeModal
                        isOpen={Boolean(closing)}
                        patientId={patientId}
                        programme={closing}
                        appointmentId={ownVisit && appointment?.status !== 'Cancelled' ? appointmentId : null}
                        onClose={() => setClosing(null)}
                    />
                </>
            )}
        </section>
    );
}

export default ProgrammeCard;
